import React, { useState } from 'react';
import { Sparkles, ArrowRight, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useServiceGeneration } from '@/hooks/services/use-service-generation';
import { cn } from '@/lib/utils';

interface CreateWorkOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  workbookId: string;
}

export const CreateWorkOrderModal: React.FC<CreateWorkOrderModalProps> = ({
  isOpen,
  onClose,
  workbookId,
}) => {
  const [currentQuestion, setCurrentQuestion] = useState<1 | 2>(1);
  const [problemStatement, setProblemStatement] = useState('');
  const [solutionDescription, setSolutionDescription] = useState('');
  const { generateService, isGenerating } = useServiceGeneration();

  const handleNextQuestion = () => {
    if (currentQuestion === 1 && problemStatement.trim()) {
      setCurrentQuestion(2);
    }
  };

  const handleSubmitService = async () => {
    if (!problemStatement.trim() || !solutionDescription.trim()) {
      return;
    }

    try {
      await generateService(
        {
          problemStatement,
          solutionDescription,
        },
        workbookId
      );
      
      // Reset form and close modal
      setProblemStatement('');
      setSolutionDescription('');
      setCurrentQuestion(1);
      onClose();
    } catch (error) {
      // Error is handled in the hook
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setProblemStatement('');
    setSolutionDescription('');
    setCurrentQuestion(1);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create Work Order</DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-6">
          {/* Progress Indicator */}
          <div className="flex items-center gap-2">
            <div className={cn(
              "flex-1 h-2 rounded-full transition-colors",
              currentQuestion >= 1 ? "bg-purple-600" : "bg-gray-200 dark:bg-gray-700"
            )} />
            <div className={cn(
              "flex-1 h-2 rounded-full transition-colors",
              currentQuestion >= 2 ? "bg-purple-600" : "bg-gray-200 dark:bg-gray-700"
            )} />
          </div>

          <AnimatePresence mode="wait">
            {/* Question 1 */}
            {currentQuestion === 1 && (
              <motion.div
                key="question1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div>
                  <Label className="text-base font-medium mb-2 block">
                    What problem are you trying to solve?
                  </Label>
                  <Textarea
                    value={problemStatement}
                    onChange={(e) => setProblemStatement(e.target.value)}
                    placeholder="Describe the challenge or pain point you're facing..."
                    className="min-h-[120px] w-full"
                    autoFocus
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={handleClose}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleNextQuestion}
                    disabled={!problemStatement.trim()}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                  >
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Question 2 */}
            {currentQuestion === 2 && (
              <motion.div
                key="question2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div>
                  <Label className="text-base font-medium mb-2 block">
                    What's your ideal solution look like?
                  </Label>
                  <Textarea
                    value={solutionDescription}
                    onChange={(e) => setSolutionDescription(e.target.value)}
                    placeholder="Describe what success looks like for you..."
                    className="min-h-[120px] w-full"
                    autoFocus
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentQuestion(1)}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleSubmitService}
                    disabled={!solutionDescription.trim() || isGenerating}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                  >
                    {isGenerating ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="mr-2"
                        >
                          <Sparkles className="h-4 w-4" />
                        </motion.div>
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Create Service
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
};